import type { ImageLoaderProps } from "next/image";

/**
 * Custom Next.js image loader that uses Shopify CDN's built-in image
 * transformation API instead of downloading the original and re-optimizing
 * it through Next.js. This avoids timeout errors on large images.
 *
 * Shopify CDN supports: ?width=, ?height=, &crop=center, &format=auto
 */
export default function shopifyImageLoader({
	src,
	width,
	quality,
}: ImageLoaderProps): string {
	// Keep local/relative/data/blob URLs unchanged (e.g. /svg/facebook.svg)
	if (
		src.startsWith("/") ||
		src.startsWith("data:") ||
		src.startsWith("blob:")
	) {
		return src;
	}

	// For non-absolute URLs, fail safe by returning as-is.
	if (!/^https?:\/\//i.test(src)) {
		return src;
	}

	// Transform non-Shopify absolute URLs with generic width/quality params.
	if (!src.includes("cdn.shopify.com")) {
		const url = new URL(src);
		url.searchParams.set("w", String(width));
		if (quality) url.searchParams.set("q", String(quality));
		return url.toString();
	}

	const url = new URL(src);
	// Remove any existing version/width params
	url.searchParams.delete("v");
	url.searchParams.delete("width");
	url.searchParams.delete("height");

	// Use Shopify CDN image transformation
	url.searchParams.set("width", String(width));
	url.searchParams.set("quality", String(quality ?? 70));
	url.searchParams.set("format", "auto");

	return url.toString();
}
